import {
  createContext,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useNavigate } from 'react-router'
import { useQueryClient } from '@tanstack/react-query'

import { useCurrentUser } from '@/hooks/useAuth'

import { useNegotiateConnection } from './hooks/hooks'
import { triggerNotificationToast } from './utils/notificationToast'
import { WebPubSubClientManager } from './client'
import { ConnectionStatus, NotificationSignalType } from './types'

export const WebPubSubContext = createContext<WebPubSubClientManager | null>(
  null
)

type WebPubSubProviderProps = {
  children: ReactNode
  autoConnect?: boolean
}

export const WebPubSubProvider = ({
  children,
  autoConnect = true,
}: WebPubSubProviderProps) => {
  const client = useMemo(() => WebPubSubClientManager.getInstance(), [])
  const { data: user, isLoading: userLoading } = useCurrentUser()
  const previousUserIdRef = useRef<string | undefined>(undefined)
  const [sessionId, setSessionId] = useState<string>(crypto.randomUUID())
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const {
    data: negotiateData,
    dataUpdatedAt,
    isFetching,
  } = useNegotiateConnection(
    autoConnect && !userLoading && !!user?.id,
    sessionId
  )

  useEffect(() => {
    const unsubscribe = client.onServerMessage((e) => {
      if (e.type === NotificationSignalType.ServerMessage) {
        const { title, message, notification_id, metadata, notification_type } =
          e.data.data
        triggerNotificationToast(
          title,
          message,
          navigate,
          notification_id,
          metadata,
          notification_type
        )

        // Refetch notification list to get the newest unread count
        queryClient.invalidateQueries({
          queryKey: ['notifications'],
        })
      }
    })

    return unsubscribe
  }, [client, queryClient, navigate])

  useEffect(() => {
    const wasLoggedOut = previousUserIdRef.current && !user?.id

    if (wasLoggedOut) {
      const handleLogout = async () => {
        try {
          const currentStatus = client.getConnectionStatus()

          if (
            currentStatus === ConnectionStatus.connected ||
            currentStatus === ConnectionStatus.connecting
          ) {
            await client.disconnect(true)
          }

          client.reset()

          const newSessionId = crypto.randomUUID()
          setSessionId(newSessionId)
        } catch (error) {
          console.error('Failed to disconnect from WebPubSub:', error)
        }
      }

      void handleLogout()
    }

    previousUserIdRef.current = user?.id
  }, [user?.id, client])

  useEffect(() => {
    const shouldConnect =
      autoConnect &&
      !userLoading &&
      !isFetching &&
      !!user?.id &&
      !!negotiateData

    if (!shouldConnect) {
      return
    }

    const currentStatus = client.getConnectionStatus()
    const isAlreadyConnected = [
      ConnectionStatus.connected,
      ConnectionStatus.connecting,
    ].includes(currentStatus)

    const handleConnect = async () => {
      try {
        if (isAlreadyConnected) {
          await client.disconnect()
        }
        await client.connect(negotiateData)
      } catch (error) {
        console.error('Failed to connect to WebPubSub:', error)
      }
    }

    void handleConnect()
  }, [
    autoConnect,
    userLoading,
    user?.id,
    client,
    negotiateData,
    dataUpdatedAt,
    sessionId,
    isFetching,
  ])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      void client.disconnect()
    }
  }, [client])

  return (
    <WebPubSubContext.Provider value={client}>
      {children}
    </WebPubSubContext.Provider>
  )
}
